import tenantRoutes from '@routes';
import tenantTheme from '@theme';
import tenantUtils from '@utils';
import { Typography } from 'antd';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  useDeleteListingFromDubizzleMutation,
  useDeleteListingMutation,
  useLazyListingDeleteReasonsQuery,
  useTransferListingsMutation,
} from '../../../apis/listings';
import {
  Button,
  ConfirmationModal,
  DrawerModal,
  Dropdown,
  Flex,
  Icon,
  Image,
  Spinner,
  Tag,
  TextInput,
  TextWithIcon,
  notification,
} from '../../common';
import { useRouteNavigate } from '../../../hooks';
import {
  AssigneeRadioGrid,
  coverImageInBox,
  getInitials,
  toSafeUserIdString,
} from '../../listing/change-owner/assignee-radio-grid';
import {
  CHANGE_OWNER_CARD_MAX_WIDTH,
  ChangeOwnerListingSummary,
  changeOwnerSummaryCardStyle,
} from '../../listing/change-owner/listing-summary';
import {
  BayutListingNavMark,
  ChangeOwnershipIcon,
  DubizzleFlameLogo,
  NavigationActionIcon,
} from '../../svg';
import { v2Color } from '@theme/v2';

const buildNavOptions = ({ bayutUrl, dubizzleUrl, t }) => {
  const options = [];
  if (bayutUrl) {
    options.push({
      id: 'view_bayut',
      title: t('View on Bayut'),
      href: bayutUrl,
    });
  }
  if (dubizzleUrl) {
    options.push({
      id: 'view_dubizzle',
      title: t('View on Dubizzle'),
      href: dubizzleUrl,
    });
  }
  return options;
};

const actionIconButtonStyle = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: v2Color('#F2FAFA'),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const actionDropdownStyle = {
  padding: 0,
  border: 'none',
  background: 'transparent',
  boxShadow: 'none',
};

const MOBILE_MENU_ICON_BG = v2Color('#F2FAFA');
const MOBILE_MENU_REMOVE_BG = '#FFF1EE';
const MOBILE_MENU_PRIMARY = v2Color('#006169');
const MOBILE_MENU_REMOVE_ICON = '#F73131';

const mobileMenuIconBoxStyle = {
  width: 22,
  height: 22,
  borderRadius: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/** Desktop “View on …” rows: circular well behind Bayut / Dubizzle marks. */
const listingNavWebCircleStyle = {
  width: 26,
  height: 26,
  borderRadius: '50%',
  backgroundColor: v2Color('#F2FAFA'),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const getDesktopListingNavOptionIcon = (op) => {
  if (op?.id === 'view_bayut') {
    return (
      <span style={listingNavWebCircleStyle}>
        <BayutListingNavMark size={15} />
      </span>
    );
  }
  if (op?.id === 'view_dubizzle') {
    return (
      <span style={listingNavWebCircleStyle}>
        <DubizzleFlameLogo size={15} />
      </span>
    );
  }
  return op?.icon ?? null;
};

const getDesktopMoreOptionIcon = (op) => {
  if (op?.id === 'edit') {
    return (
      <span style={listingNavWebCircleStyle}>
        <Icon icon="MdEdit" size="14px" color={tenantTheme['primary-color']} />
      </span>
    );
  }
  if (op?.id === 'change_owner') {
    return (
      <span style={listingNavWebCircleStyle}>
        <ChangeOwnershipIcon size={14} />
      </span>
    );
  }
  if (op?.id === 'remove') {
    return (
      <span style={{ ...listingNavWebCircleStyle, backgroundColor: MOBILE_MENU_REMOVE_BG }}>
        <Icon icon="HiOutlineTrash" size="14px" color={tenantTheme['error-color']} />
      </span>
    );
  }
  return op?.icon ?? null;
};

/** Mobile menuOnly dropdown: 22×22 icon wells per design (except remove uses #FFF1EE). */
const getMobileMenuOnlyOptionIcon = (op) => {
  const isRemove = op?.id === 'remove';
  const bg = isRemove ? MOBILE_MENU_REMOVE_BG : MOBILE_MENU_ICON_BG;
  const wrap = (children) => (
    <span style={{ ...mobileMenuIconBoxStyle, backgroundColor: bg }}>{children}</span>
  );

  if (op?.href && (op?.id === 'view_bayut' || op?.id === 'view_dubizzle')) {
    return wrap(
      op?.id === 'view_bayut' ? (
        <BayutListingNavMark size={12} />
      ) : (
        <DubizzleFlameLogo size={12} />
      ),
    );
  }
  switch (op?.id) {
    case 'edit':
      return wrap(<Icon icon="MdEdit" size="12px" color={MOBILE_MENU_PRIMARY} />);
    case 'change_owner':
      return wrap(<ChangeOwnershipIcon size={12} />);
    case 'remove':
      return wrap(<Icon icon="HiOutlineTrash" size="12px" color={MOBILE_MENU_REMOVE_ICON} />);
    default:
      return op?.icon ? (
        typeof op.icon === 'string' ? (
          <Icon icon={op.icon} size="1.6em" />
        ) : (
          op.icon
        )
      ) : null;
  }
};

const CHANGE_OWNER_SUMMARY_GRID_GAP = 12;
const CHANGE_OWNER_MODAL_BODY_PAD_X = 16;
const CHANGE_OWNER_MODAL_WIDTH_DESKTOP = 900;

const currentAssigneeBadgeStyle = {
  width: 99,
  height: 21,
  flexShrink: 0,
  margin: 0,
  padding: 0,
  lineHeight: '21px',
  fontSize: 11,
  fontWeight: 600,
  textAlign: 'center',
  borderRadius: 4,
  background: '#3c3c3c',
  color: '#fff',
  border: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const UnifiedListingTableActions = (props) => {
  const { t } = useTranslation();
  const { Text } = Typography;
  const navigate = useRouteNavigate();
  const { isMobile } = useSelector((state) => state.app.AppConfig);
  const { user } = useSelector((state) => state.app.loginUser);
  const agencyUsers = useSelector((state) => state.app.userGroup?.list) || [];
  const { menuOnly = false, listingRow } = props || {};
  const isAgencyOwner = user?.user_role_within_agency === 'owner';

  const listingId = props?.property_id || props?.id;

  const dispositionSlug =
    listingRow?.disposition?.slug ?? listingRow?.platforms?.unified?.disposition?.slug;
  const listingStatusSlug =
    typeof listingRow?.status === 'string' ? listingRow.status : listingRow?.status?.slug;

  const hideEditDeletedDisposition = dispositionSlug === 'deleted';
  const hideRemoveRemovedDisposition =
    dispositionSlug === 'removed' || listingStatusSlug === 'removed';
  const isRejectedDisposition = dispositionSlug === 'rejected';
  const hideMirroredActions = tenantUtils.shouldRestrictMirroredListingActions(listingRow, user);

  const bayutUrl = props?.bayut?.public_url;
  const dubizzleUrl = props?.dubizzle?.public_url;

  const navOptions = useMemo(() => buildNavOptions({ bayutUrl, dubizzleUrl, t }), [bayutUrl, dubizzleUrl, t]);

  const [deleteListing] = useDeleteListingMutation();
  const [deleteListingFromDubizzle] = useDeleteListingFromDubizzleMutation();
  const [getDeleteReasons, { data: deleteReasons, isLoading: deleteLoading }] = useLazyListingDeleteReasonsQuery();
  const [transferListings, { isLoading: transferLoading }] = useTransferListingsMutation();

  const [loading, setLoading] = useState(false);
  const [deleteFromDubizzle, setDeleteFromDubizzle] = useState(false);
  const deleteListingRef = useRef();
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherReason, setOtherReason] = useState('');
  const [deleteReasonError, setDeleteReasonError] = useState(null);

  const purposeId = props?.purposeId;

  const [changeOwnerVisible, setChangeOwnerVisible] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState(null);

  const currentListingOwnerId = useMemo(() => {
    const owner = props?.bayut?.listingOwner ?? props?.dubizzle?.listingOwner ?? props?.unified?.listingOwner;
    return toSafeUserIdString(owner?.id);
  }, [props?.bayut?.listingOwner, props?.dubizzle?.listingOwner, props?.unified?.listingOwner]);

  const listingOwnerFromPlatforms = useMemo(
    () => props?.bayut?.listingOwner ?? props?.dubizzle?.listingOwner ?? props?.unified?.listingOwner,
    [props?.bayut?.listingOwner, props?.dubizzle?.listingOwner, props?.unified?.listingOwner],
  );

  const currentAssignee = useMemo(() => {
    const raw = listingOwnerFromPlatforms;
    const ownerKey = toSafeUserIdString(raw?.id);
    if (ownerKey == null) return null;
    const fromGroup = agencyUsers.find((u) => toSafeUserIdString(u?.id) === ownerKey);
    return {
      id: raw.id,
      name: tenantUtils.getLocalisedString(raw, 'name') || fromGroup?.name || raw?.name || '—',
      email: raw?.email || fromGroup?.email,
      mobile: raw?.mobile ?? raw?.phone ?? fromGroup?.mobile ?? fromGroup?.phone,
      profile_image: raw?.profile_image || fromGroup?.profile_image,
      activeListings: fromGroup?.active_listings_count ?? fromGroup?.listings_count,
    };
  }, [listingOwnerFromPlatforms, agencyUsers]);

  const platformListingMeta = useMemo(() => {
    const b = props?.bayut?.platform_listing_id;
    const d = props?.dubizzle?.platform_listing_id;
    const u = props?.unified?.platform_listing_id;
    if (b) return { label: t('Listing ID'), value: b };
    if (d) return { label: t('Listing ID'), value: d };
    if (u) return { label: t('Listing ID'), value: u };
    return { label: t('Listing ID'), value: listingId };
  }, [props?.bayut?.platform_listing_id, props?.dubizzle?.platform_listing_id, props?.unified?.platform_listing_id, listingId, t]);

  const openChangeOwner = useCallback(() => {
    setNewOwnerId(null);
    setChangeOwnerVisible(true);
  }, []);

  const resolveTransferUserId = useCallback(() => {
    const key = newOwnerId;
    if (key == null) return null;
    const fromList = agencyUsers.find((u) => toSafeUserIdString(u?.id) === key);
    if (fromList != null) {
      const n = Number(fromList.id);
      return Number.isFinite(n) ? n : null;
    }
    if (listingOwnerFromPlatforms?.id != null && toSafeUserIdString(listingOwnerFromPlatforms.id) === key) {
      const n = Number(listingOwnerFromPlatforms.id);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }, [agencyUsers, listingOwnerFromPlatforms?.id, newOwnerId]);

  const onRemove = () => {
    setDeleteFromDubizzle(!bayutUrl && !!dubizzleUrl);
    deleteListingRef?.current && deleteListingRef.current?.showModal();
    getDeleteReasons(purposeId);
  };

  const onDelete = async () => {
    if (!selectedReason) {
      setDeleteReasonError(t('Please add reason to delete your listing.'));
      return;
    }
    if (selectedReason?.is_text_required && !otherReason.trim()) {
      setDeleteReasonError(t('Please add reason to delete your listing.'));
      return;
    }

    const payload = {
      reason_id: selectedReason?.id,
      reason_text: selectedReason?.slug === 'other' ? otherReason : selectedReason.name,
    };

    setLoading(true);
    !!deleteListingRef?.current && deleteListingRef.current.setLoading(true);
    const response = deleteFromDubizzle
      ? await deleteListingFromDubizzle({
          listingId,
          reason_id: payload?.reason_id,
          reason_text: payload?.reason_text,
        })
      : await deleteListing({
          listingId,
          reason_id: payload?.reason_id,
          reason_text: payload?.reason_text,
        });

    if (response?.error) {
      notification.error(response.error);
    } else {
      notification.success(t('Listing has been deleted!'));
      deleteListingRef?.current && deleteListingRef.current.hideModal();
      setDeleteReasonError(null);
      setSelectedReason(null);
      setOtherReason('');
    }

    setLoading(false);
    !!deleteListingRef?.current && deleteListingRef.current.setLoading(false);
  };

  const onCancelDeleteModal = () => {
    setDeleteReasonError(null);
    setSelectedReason(null);
    setOtherReason('');
    setDeleteFromDubizzle(false);
  };

  const moreOptions = useMemo(() => {
    const opts = [];
    if (isAgencyOwner) {
      opts.push({
        id: 'change_owner',
        title: t('Change Owner'),
        onClick: openChangeOwner,
      });
    }
    if (!hideRemoveRemovedDisposition && !hideMirroredActions) {
      opts.push({
        id: 'remove',
        title: <span style={{ color: tenantTheme['error-color'] }}>{t('Remove')}</span>,
        onClick: onRemove,
      });
    }
    return opts;
  }, [hideRemoveRemovedDisposition, hideMirroredActions, isAgencyOwner, openChangeOwner, onRemove, t]);

  // `action`, not `onClick`: Dropdown also binds an option's onClick to its label, so one click ran it twice.
  const menuOnlyOptions = useMemo(() => {
    const opts = [];
    if (!hideEditDeletedDisposition && !hideMirroredActions) {
      opts.push({
        id: 'edit',
        title: t('Edit'),
        icon: 'MdEdit',
        action: () => navigate(`${tenantRoutes.app().post_listing.path}/${listingId}`),
      });
    }
    if (isAgencyOwner) {
      opts.push({
        id: 'change_owner',
        title: t('Change Owner'),
        icon: 'ChangeOwnershipIcon',
        action: openChangeOwner,
      });
    }
    if (bayutUrl) {
      opts.push({ id: 'view_bayut', title: t('View on Bayut'), href: bayutUrl });
    }
    if (dubizzleUrl) {
      opts.push({
        id: 'view_dubizzle',
        title: t('View on Dubizzle'),
        href: dubizzleUrl,
      });
    }
    if (!hideRemoveRemovedDisposition && !hideMirroredActions) {
      opts.push({
        id: 'remove',
        title: t('Remove'),
        icon: 'HiOutlineTrash',
        danger: true,
        action: onRemove,
      });
    }
    return opts;
  }, [
    bayutUrl,
    dubizzleUrl,
    hideEditDeletedDisposition,
    hideRemoveRemovedDisposition,
    hideMirroredActions,
    isAgencyOwner,
    listingId,
    navigate,
    onRemove,
    openChangeOwner,
    t,
  ]);

  if (isRejectedDisposition) return null;

  return (
    <>
      <DrawerModal
        title={t('Change Listing Ownership')}
        visible={changeOwnerVisible}
        onCancel={() => {
          setChangeOwnerVisible(false);
          setNewOwnerId(null);
        }}
        footer={null}
        width={isMobile ? undefined : CHANGE_OWNER_MODAL_WIDTH_DESKTOP}
        height={isMobile ? '88vh' : 'auto'}
        bodyStyle={
          isMobile
            ? { minHeight: 200, maxHeight: 'calc(88vh - 120px)', overflowY: 'auto', padding: 12 }
            : {
                maxHeight: 'min(78vh, 720px)',
                overflowY: 'auto',
                padding: `8px ${CHANGE_OWNER_MODAL_BODY_PAD_X}px 16px`,
              }
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : `repeat(2, minmax(0, ${CHANGE_OWNER_CARD_MAX_WIDTH}px))`,
              gap: CHANGE_OWNER_SUMMARY_GRID_GAP,
              justifyContent: isMobile ? 'stretch' : 'start',
            }}
          >
            <div style={{ ...changeOwnerSummaryCardStyle, ...(isMobile ? { maxWidth: '100%' } : {}) }}>
              <ChangeOwnerListingSummary
                listingRow={listingRow}
                idLabel={platformListingMeta.label}
                idValue={platformListingMeta.value}
              />
            </div>

            <div style={{ ...changeOwnerSummaryCardStyle, ...(isMobile ? { maxWidth: '100%' } : {}) }}>
              {currentAssignee ? (
                <>
                  <Flex align="flex-start" gap={'12px'} style={{ width: '100%' }}>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: '#e8e8e8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 8,
                        lineHeight: 1,
                        color: tenantTheme['primary-text-color'],
                      }}
                    >
                      {currentAssignee.profile_image ? (
                        <Image src={currentAssignee.profile_image} {...coverImageInBox} />
                      ) : (
                        getInitials(currentAssignee.name)
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <Text strong ellipsis style={{ fontSize: 16, lineHeight: '21px', margin: 0, flex: 1, minWidth: 0 }}>
                        {currentAssignee.name}
                      </Text>
                      <span style={currentAssigneeBadgeStyle}>{t('Current Assignee')}</span>
                    </div>
                  </Flex>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: 10, width: '100%' }}>
                    {currentAssignee.activeListings != null && (
                      <TextWithIcon
                        icon="FaList"
                        gap="8px"
                        iconProps={{ size: 12, color: tenantTheme.gray500 }}
                        value={`${currentAssignee.activeListings} ${t('Active Listings')}`}
                        textStyle={{ fontSize: 12, lineHeight: 1.3 }}
                      />
                    )}
                    {!!currentAssignee.email && (
                      <TextWithIcon
                        icon="MdEmail"
                        gap="8px"
                        iconProps={{ size: 12, color: tenantTheme.gray500 }}
                        value={currentAssignee.email}
                        textStyle={{ fontSize: 12, lineHeight: 1.3 }}
                      />
                    )}
                    {!!currentAssignee.mobile && (
                      <TextWithIcon
                        icon="MdPhone"
                        gap="8px"
                        iconProps={{ size: 12, color: tenantTheme.gray500 }}
                        value={currentAssignee.mobile}
                        textStyle={{ fontSize: 12, lineHeight: 1.3 }}
                      />
                    )}
                  </div>
                </>
              ) : (
                <Text type="secondary">{t('No assignee information available.')}</Text>
              )}
            </div>
          </div>

          <div>
            <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>{t('Select the new assignee')}</div>
            <AssigneeRadioGrid
              users={agencyUsers}
              value={newOwnerId}
              onChange={setNewOwnerId}
              disabledUserId={currentListingOwnerId}
            />
          </div>

          <Flex justify="flex-end" gap="8px" style={{ marginTop: 4, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
            <Button
              type="default"
              onClick={() => {
                setChangeOwnerVisible(false);
                setNewOwnerId(null);
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              type="primary"
              loading={transferLoading}
              disabled={
                !newOwnerId ||
                (currentListingOwnerId != null && newOwnerId === currentListingOwnerId)
              }
              onClick={async () => {
                const userId = resolveTransferUserId();
                const numericListingId = Number(listingId);
                if (userId == null || !Number.isFinite(numericListingId)) {
                  notification.error(t('Please select a valid user.'));
                  return;
                }
                if (
                  currentListingOwnerId != null &&
                  String(userId) === String(currentListingOwnerId)
                ) {
                  notification.error(t('Please select a different assignee.'));
                  return;
                }
                const response = await transferListings({
                  userId,
                  listingIds: [numericListingId],
                });
                if (response?.error) {
                  notification.error(response?.error);
                  return;
                }
                notification.success(t('Updated Successfully'));
                setChangeOwnerVisible(false);
                setNewOwnerId(null);
              }}
            >
              {t('Change Ownership')}
            </Button>
          </Flex>
        </div>
      </DrawerModal>

      <ConfirmationModal
        ref={deleteListingRef}
        onSuccess={onDelete}
        title={t('Delete Listing')}
        type="danger"
        okText={t('Delete')}
        onCancel={onCancelDeleteModal}
        loading={deleteLoading || loading}
        bodyStyle={{ padding: isMobile ? 16 : 24 }}
        destroyOnClose
        footerError={deleteReasonError && selectedReason?.slug != 'other' && deleteReasonError}
      >
        <div className="mb-16" style={{ fontWeight: 600 }}>
          {t('Why are you deleting your listing?')}
        </div>

        <div className="d-flex flex-column mb-8" style={{ gap: '16px' }}>
          {deleteLoading ? (
            <Spinner />
          ) : deleteReasons?.length ? (
            deleteReasons.map((option) => (
              <div
                key={option?.id}
                className="color-gray-dark"
                style={{ display: 'flex', gap: '10px', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => {
                  setSelectedReason(option);
                  setDeleteReasonError(null);
                }}
              >
                <input type="radio" checked={selectedReason?.id === option?.id} readOnly />
                <div>{option?.name}</div>
              </div>
            ))
          ) : null}
        </div>

        {selectedReason?.slug == 'other' && (
          <TextInput
            value={otherReason}
            handleChange={(e) => setOtherReason(e.target.value)}
            errorMsg={deleteReasonError}
            lineCount={3}
            placeholder={t('Please enter your reason')}
          />
        )}
      </ConfirmationModal>

      {menuOnly ? (
        <Dropdown
          placement="bottomRight"
          options={menuOnlyOptions}
          getOptionLabel={(op) => op?.title}
          getOptionValue={(op) => op?.id}
          getOptionIcon={
            isMobile
              ? getMobileMenuOnlyOptionIcon
              : (op) => getDesktopMoreOptionIcon(op) || getDesktopListingNavOptionIcon(op)
          }
          onChange={(op) => {
            if (op?.href) window?.open?.(op.href, '_blank', 'noopener,noreferrer');
            else op?.action?.();
          }}
          suffixIcon={null}
          style={actionDropdownStyle}
        >
          <div style={actionIconButtonStyle}>
            <Icon icon="MdMoreVert" size="16px" color={tenantTheme['primary-color']} />
          </div>
        </Dropdown>
      ) : (
      <Flex gap="8px" wrap={false} justify="flex-end" align="center">
        <Dropdown
          placement="bottomRight"
          options={navOptions}
          getOptionLabel={(op) => op?.title}
          getOptionValue={(op) => op?.id}
          getOptionLink={(op) => op?.href}
          getOptionIcon={getDesktopListingNavOptionIcon}
          useAsLink
          className="unified-listing-nav-dropdown"
          suffixIcon={null}
          style={actionDropdownStyle}
        >
          <div style={actionIconButtonStyle}>
            <NavigationActionIcon size={12} />
          </div>
        </Dropdown>

        {!hideEditDeletedDisposition && !hideMirroredActions && (
          <div
            style={actionIconButtonStyle}
            onClick={(e) => {
              e?.stopPropagation?.();
              navigate(`${tenantRoutes.app().post_listing.path}/${listingId}`);
            }}
          >
            <Icon icon="MdEdit" size="16px" color={tenantTheme['primary-color']} />
          </div>
        )}

        {moreOptions?.length > 0 && (
        <Dropdown
          placement="bottomRight"
          options={moreOptions}
          getOptionLabel={(op) => op?.title}
          getOptionValue={(op) => op?.id}
          getOptionIcon={getDesktopMoreOptionIcon}
          onChange={(op) => op?.onClick?.()}
          suffixIcon={null}
          style={actionDropdownStyle}
        >
          <div style={actionIconButtonStyle}>
            <Icon icon="MdMoreVert" size="16px" color={tenantTheme['primary-color']} />
          </div>
        </Dropdown>
        )}
      </Flex>
      )}
    </>
  );
};

export default UnifiedListingTableActions;

